import { PlaycastHostEdition } from '@playcastdotio/CoreTypes';
import {
  PlaycastMessage,
  PlaycastMessageProcessAuth,
  PlaycastMessageProcessDisconnect,
  PlaycastMessageProcessEnumRunning,
  PlaycastMessageProcessGetPolicy,
  PlaycastMessageProcessLaunch,
  PlaycastMessageProcessRestart,
  PlaycastMessageProcessSelect,
  PlaycastMessageProcessSetFocusToLegendaryWindow,
  PlaycastMessageProcessSetPolicy,
  PlaycastMessageProcessTerminate,
  PlaycastMessageSource,
  ProcessLaunchPackage,
} from '../types';
import { formatMessage } from './_util/formatMessage';
import type { MacroRunCommand } from '../types/api/macroExecution';
import type { AnalysisCommand } from '../types/api/macroCalibration';
import type { ReferenceCaptureCommand } from '../types/api/macroReferenceCapture';
import type { PlaycastMessageProcessMacroRun } from '../types/messages/process';
export { macroPackageRun, macroPackageRunReply } from './macroPackageProcess';

export const macroRun = (
  command: MacroRunCommand | AnalysisCommand | ReferenceCaptureCommand,
): PlaycastMessage<PlaycastMessageProcessMacroRun> =>
  formatMessage('host', 'host', {
    target: 'process',
    action: 'macroRun',
    isReply: false,
    message: command,
  }) as PlaycastMessage<PlaycastMessageProcessMacroRun>;

const target = 'process';

/**
 * Create a process launch message with the new launchPackage format
 * 
 * @param hostId - Host authentication ID
 * @param edition - Host edition ('standard' or 'pavilion')
 * @param launchPackage - Launch package containing launcher-specific configuration
 * @param userAuth - Optional user authentication token
 */
export const processLaunch = (
  hostId: string,
  edition: PlaycastHostEdition,
  launchPackage: ProcessLaunchPackage,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageProcessLaunch> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageProcessLaunch = {
    target,
    action: 'launch',
    isReply: false,
    message: {
      edition,
      launchPackage,
    },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageProcessLaunch>;
};

export const processRestart = (
  hostId: string,
  argument: string,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageProcessRestart> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageProcessRestart = {
    target,
    action: 'restart',
    isReply: false,
    message: {
      argument,
    },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageProcessRestart>;
}

export const processTerminate = (
  hostId: string,
  type: 'default' | 'process' | PlaycastHostEdition,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageProcessTerminate> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageProcessTerminate = {
    target,
    action: 'terminate',
    isReply: false,
    message: {
      type,
    },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageProcessTerminate>;
};

export const processSetPolicy = (
  hostId: string,
  nonFocusInput: boolean,
  constrainMouse: boolean,
  allowToolbarAccess: boolean,
  forceProcessOnTop: boolean,
  launchTimeout: number,
  captureAudio: boolean,
  captureVideo: boolean,
  saveEnumeratedApps: boolean,
  onlyExitOnDisconnect: boolean,
  enableHostDominanceKick?: boolean,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageProcessSetPolicy> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageProcessSetPolicy = {
    target,
    action: 'setPolicy',
    isReply: false,
    message: {
      nonFocusInput,
      constrainMouse,
      allowToolbarAccess,
      forceProcessOnTop,
      launchTimeout,
      captureAudio,
      captureVideo,
      saveEnumeratedApps,
      onlyExitOnDisconnect,
      enableHostDominanceKick,
    },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageProcessSetPolicy>;
};

export const processGetPolicy = (hostId: string, userAuth?: string): PlaycastMessage<PlaycastMessageProcessGetPolicy> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageProcessGetPolicy = {
    target,
    action: 'getPolicy',
    isReply: false,
    message: {},
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageProcessGetPolicy>;
};

export const processEnumRunning = (hostId: string, userAuth?: string, captureElementName?: string,
  captureElementRequestId?: string): PlaycastMessage<PlaycastMessageProcessEnumRunning> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageProcessEnumRunning = {
    target,
    action: 'enumRunning',
    isReply: false,
    message: captureElementName === undefined ? {} : { captureElementName, captureElementRequestId },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageProcessEnumRunning>;
};

export const processSelect = (hostId: string, processId: number, userAuth?: string, captureMode?: 'window', windowHandle?: string): PlaycastMessage<PlaycastMessageProcessSelect> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageProcessSelect = {
    target,
    action: 'select',
    isReply: false,
    message: {
      processId,
      ...(captureMode ? { captureMode } : {}),
      ...(windowHandle ? { windowHandle } : {}),
    },
  };

  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageProcessSelect>;
};

export const processDisconnect = (hostId: string, userAuth?: string): PlaycastMessage<PlaycastMessageProcessDisconnect> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageProcessDisconnect = {
    target,
    action: 'disconnect',
    isReply: false,
    message: {},
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageProcessDisconnect>;
};

/**
 * Create a process auth message for guest mode Steam authentication via Playjector
 * 
 * Note: Epic authentication is handled directly by the host, not Playjector
 * 
 * @param username - The Steam account username
 * @param password - The Steam account password
 */
export const processSteamAuth = (
  username: string,
  password: string,
): PlaycastMessage<PlaycastMessageProcessAuth> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageProcessAuth = {
    target,
    action: 'auth',
    isReply: false,
    message: {
      launcher: 'steam',
      username,
      password,
    },
  };
  return formatMessage('', messageSource, message) as PlaycastMessage<PlaycastMessageProcessAuth>;
};

export const processSetFocusToLegendaryWindow = (
  hostId = '',
  userAuth?: string,
): PlaycastMessage<PlaycastMessageProcessSetFocusToLegendaryWindow> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageProcessSetFocusToLegendaryWindow = {
    target,
    action: 'setFocusToLegendaryWindow',
    isReply: false,
    message: {},
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageProcessSetFocusToLegendaryWindow>;
};
